import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Icon,
  alpha,
  useTheme,
  ButtonBase,
  ClickAwayListener,
  Popper,
  Paper,
  MenuList,
  CircularProgress,
  Alert,
} from "@mui/material";
import { toast } from "react-toastify";
import { accountingService } from "../../../services/accounting";
import { IBranch, IListBranchesRequest } from "../accounting.interface";

interface BranchSelectorProps {
  selectedBranch: string;
  onBranchChange: (branchId: string) => void;
  size?: "small" | "medium";
  showLabel?: boolean;
  variant?: "outlined" | "standard" | "filled" | "compact";
}

// Mock organization data until real API is integrated
const organizationData = {
  id: "organization",
  name: "Organization Level",
  description: "All Branches Combined",
};

export const BranchSelector: React.FC<BranchSelectorProps> = ({
  selectedBranch,
  onBranchChange,
  size = "small",
  showLabel = false,
  variant = "outlined",
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [branches, setBranches] = useState<IBranch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch branches from API
  const fetchBranches = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const requestData: IListBranchesRequest = {
        sortField: "name",
        sortOrder: "asc",
      };

      const response = await accountingService.listBranches(requestData);
      setBranches(
        response.data.branches.filter((branch) => !branch.is_deleted)
      );
    } catch (error: any) {
      console.error("Failed to fetch branches:", error);
      setError("Failed to load branches");
      toast.error("Failed to load branches");
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch branches on component mount
  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (branchId: string) => {
    onBranchChange(branchId);
    handleClose();
  };

  const getCurrentSelection = () => {
    if (selectedBranch === "organization") {
      return organizationData;
    }
    return branches.find((branch) => branch.branch_id === selectedBranch);
  };

  // Helper function to render selected value
  const renderSelectedValue = (value: string) => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading...</Typography>
        </Box>
      );
    }

    if (value === "organization") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: "primary.main", fontSize: 18 }}>business</Icon>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {organizationData.name}
          </Typography>
        </Box>
      );
    }

    const branch = branches.find((b) => b.branch_id === value);
    if (branch) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: "info.main", fontSize: 18 }}>store</Icon>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {branch.name}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Select Branch
      </Typography>
    );
  };

  const isOpen = Boolean(anchorEl);
  const currentSelection = getCurrentSelection();

  if (variant === "standard") {
    return (
      <FormControl size={size} fullWidth>
        {showLabel && (
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Branch/Entity
          </Typography>
        )}
        <Select
          value={selectedBranch}
          onChange={(e: SelectChangeEvent) => onBranchChange(e.target.value)}
          disabled={isLoading}
          renderValue={renderSelectedValue}
        >
          <MenuItem value="organization">
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Icon sx={{ color: "primary.main" }}>business</Icon>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {organizationData.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {organizationData.description}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          {branches.map((branch) => (
            <MenuItem key={branch.branch_id} value={branch.branch_id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Icon sx={{ color: "info.main" }}>store</Icon>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {branch.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {branch.address}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <CircularProgress size={16} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </FormControl>
    );
  }

  if (variant === "compact") {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={{ position: "relative" }}>
          <ButtonBase
            onClick={handleClick}
            disabled={isLoading}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              bgcolor: "background.paper",
              minWidth: 200,
              justifyContent: "space-between",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderColor: "primary.main",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon
                sx={{
                  fontSize: 18,
                  color:
                    selectedBranch === "organization"
                      ? "primary.main"
                      : "info.main",
                }}
              >
                {selectedBranch === "organization" ? "business" : "store"}
              </Icon>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.primary" }}
              >
                {isLoading
                  ? "Loading..."
                  : currentSelection?.name || "Select Branch"}
              </Typography>
            </Box>
            {isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Icon sx={{ fontSize: 16, color: "text.secondary" }}>
                expand_more
              </Icon>
            )}
          </ButtonBase>

          <Popper
            open={isOpen}
            anchorEl={anchorEl}
            placement="bottom-start"
            sx={{ zIndex: 1300, minWidth: 250 }}
          >
            <Paper
              elevation={8}
              sx={{
                mt: 0.5,
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                overflow: "hidden",
              }}
            >
              <MenuList sx={{ py: 1 }}>
                <MenuItem
                  onClick={() => handleMenuItemClick("organization")}
                  selected={selectedBranch === "organization"}
                  sx={{
                    py: 1.5,
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Icon sx={{ color: "primary.main" }}>business</Icon>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {organizationData.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {organizationData.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>

                {error ? (
                  <MenuItem disabled>
                    <Alert severity="error" sx={{ width: "100%" }}>
                      {error}
                    </Alert>
                  </MenuItem>
                ) : (
                  branches.map((branch) => (
                    <MenuItem
                      key={branch.branch_id}
                      onClick={() => handleMenuItemClick(branch.branch_id)}
                      selected={selectedBranch === branch.branch_id}
                      sx={{
                        py: 1.5,
                        "&.Mui-selected": {
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Icon sx={{ color: "info.main" }}>store</Icon>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {branch.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {branch.address}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </MenuList>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    );
  }

  // Default "outlined" variant
  return (
    <FormControl size={size} fullWidth variant="outlined">
      {showLabel && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Branch/Entity
        </Typography>
      )}
      <Select
        value={selectedBranch}
        onChange={(e: SelectChangeEvent) => onBranchChange(e.target.value)}
        disabled={isLoading}
        renderValue={renderSelectedValue}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        }}
      >
        <MenuItem value="organization">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Icon sx={{ color: "primary.main" }}>business</Icon>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {organizationData.name}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {organizationData.description}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        {branches.map((branch) => (
          <MenuItem key={branch.branch_id} value={branch.branch_id}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Icon sx={{ color: "info.main" }}>store</Icon>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {branch.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {branch.address}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </FormControl>
  );
};
